import React, {useEffect, useState} from "react";
import {Edit, ToggleLeft, ToggleRight, Trash2, ShoppingBag} from "lucide-react";
import {api} from "../../config/api";
import secureStorage from "../../utils/secureStorage";
import {useQuery, useMutation, useQueryClient} from "@tanstack/react-query";
import ConfirmationDialog from "../Common/ConfirmationDialog";
import {SettingsToolbar, SelectionBar, SettingsTable, StatusBadge, AddAction, ListLoader} from "./SettingsUI";

const SalesChannelsList = ({
    searchTerm,
    setSearchTerm,
    debouncedSearchTerm,
    showNotification,
    onEditChannel,
    onAddChannel,
    selectedItems,
    setSelectedItems,
    selectedAll,
    setSelectedAll
}) => {
    const queryClient = useQueryClient();
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [itemsToDelete, setItemsToDelete] = useState([]);


    // Reset selection when search changes
    useEffect(() => {
        setSelectedItems([]);
        setSelectedAll(false);
    }, [debouncedSearchTerm, setSelectedItems, setSelectedAll]);

    // Fetch sales channels
    const fetchSalesChannels = async () => {
        const accountId = await secureStorage.getItem("selectedAccount");
        const response = await api.get("/sales-channels", {
            headers: {
                "X-account-id": accountId.id,
                Accept: "application/json",
            },
        });
        return response.data;
    };

    const {data: channels, isLoading, error} = useQuery({
        queryKey: ["salesChannels"],
        queryFn: fetchSalesChannels,
    });

    // Toggle active status
    const toggleChannelStatus = useMutation({
        mutationFn: async (channel) => {
            const accountId = await secureStorage.getItem("selectedAccount");
            await api.put(`/sales-channels/${channel.id}`, {active: !channel.active}, {
                headers: {
                    "X-account-id": accountId.id,
                    "Content-Type": "application/json",
                },
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["salesChannels"]});
            showNotification("success", "Statut du canal mis à jour avec succès");
        },
        onError: () => {
            showNotification("error", "Erreur lors de la mise à jour du statut");
        },
    });

    // Delete channels mutation
    const deleteChannelsMutation = useMutation({
        mutationFn: async (channelIds) => {
            const accountId = await secureStorage.getItem("selectedAccount");
            const results = await Promise.all(
                channelIds.map(id =>
                    api.delete(`/sales-channels/${id}`, {
                        headers: {
                            'X-account-id': accountId.id,
                            'Accept': 'application/json'
                        }
                    }).catch(err => ({error: err, id}))
                )
            );
            const failures = results.filter(r => r && r.error);
            if (failures.length > 0) {
                if (failures.length === channelIds.length) {
                    throw new Error("Impossible de supprimer les canaux sélectionnés");
                }
                throw new Error(`${failures.length} sur ${channelIds.length} canaux n'ont pas pu être supprimés`);
            }
            return channelIds;
        },
        onMutate: () => {
            showNotification('loading', 'Suppression en cours...');
        },
        onSuccess: () => {
            setSelectedItems([]);
            setSelectedAll(false);
            queryClient.invalidateQueries({queryKey: ['salesChannels']});
            showNotification('success', 'Canaux de vente supprimés avec succès');
            setShowConfirmDialog(false);
            setItemsToDelete([]);
        },
        onError: (error) => {
            showNotification('error', `Erreur: ${error.message}`);
            setShowConfirmDialog(false);
            setItemsToDelete([]);
        }
    });

    // Handle select all checkbox
    const handleSelectAll = () => {
        const newSelectedAll = !selectedAll;
        setSelectedAll(newSelectedAll);
        setSelectedItems(newSelectedAll ? filteredChannels.map(item => item.id) : []);
    };

    // Handle individual item selection
    const toggleItem = (channelId) => {
        setSelectedItems((prevSelected) =>
            prevSelected.includes(channelId)
                ? prevSelected.filter((id) => id !== channelId)
                : [...prevSelected, channelId]
        );
    };

    // ids awaiting confirmation: the selection, or a single row from its menu
    const askDelete = (ids) => {
        setItemsToDelete(ids);
        setShowConfirmDialog(true);
    };

    const handleConfirmDelete = () => {
        deleteChannelsMutation.mutate(itemsToDelete);
    };

    const handleCancelDelete = () => {
        setShowConfirmDialog(false);
        setItemsToDelete([]);
    };

    // Handle response format { data: [...], pagination: {...} }
    const channelsList = channels?.data || [];

    // Filter by search term
    const filteredChannels = channelsList.filter(channel =>
        channel.name?.toLowerCase().includes((debouncedSearchTerm || '').toLowerCase()) ||
        channel.description?.toLowerCase().includes((debouncedSearchTerm || '').toLowerCase())
    );

    const rowActions = (channel) => [
        {label: 'Modifier', icon: Edit, onClick: () => onEditChannel(channel), tone: 'blue'},
        channel.active
            ? {label: 'Désactiver', icon: ToggleLeft, onClick: () => toggleChannelStatus.mutate(channel), tone: 'blue'}
            : {label: 'Activer', icon: ToggleRight, onClick: () => toggleChannelStatus.mutate(channel), tone: 'green'},
        {label: 'Supprimer', icon: Trash2, onClick: () => askDelete([channel.id]), tone: 'red', danger: true},
    ];

    const columns = [
        {key: 'name', label: 'Canal', render: (channel) => (
            <span className="flex items-center gap-3 min-w-0">
                {channel.imageUrl ? (
                    <img src={channel.imageUrl} alt="" className="w-12 h-12 rounded-lg object-cover shadow-sm flex-shrink-0"/>
                ) : (
                    <span className="w-12 h-12 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                        <ShoppingBag className="w-5 h-5 text-gray-400"/>
                    </span>
                )}
                <span className="truncate">{channel.name}</span>
            </span>
        )},
        {key: 'description', label: 'Description', className: 'max-w-xs truncate', render: (channel) => channel.description || '-'},
        {key: 'active', label: 'Statut', render: (channel) => <StatusBadge active={channel.active}/>},
    ];

    return (
        <div>
            <AddAction onClick={onAddChannel}/>
            <SettingsToolbar search={searchTerm} onSearch={setSearchTerm} placeholder="Rechercher un canal de vente..."/>

            <SelectionBar count={selectedItems.length} singular="canal sélectionné" plural="canaux sélectionnés"
                          onDelete={() => askDelete(selectedItems)} deleting={deleteChannelsMutation.isPending}/>

            {isLoading ? (
                <ListLoader label="Chargement des canaux de vente..."/>
            ) : error ? (
                <div className="p-4 text-center text-red-500">Erreur : {error.message}</div>
            ) : (
                <SettingsTable
                    columns={columns}
                    rows={filteredChannels}
                    selection={{selected: selectedItems, onToggle: toggleItem, allSelected: selectedAll, onToggleAll: handleSelectAll}}
                    rowActions={rowActions}
                    onRowClick={onEditChannel}
                    empty={{
                        icon: ShoppingBag,
                        title: debouncedSearchTerm ? 'Aucun canal trouvé' : 'Aucun canal de vente',
                        text: debouncedSearchTerm ? 'Ajustez la recherche.' : 'Ajoutez vos canaux (sur place, livraison, plateformes…).',
                    }}
                />
            )}

            {showConfirmDialog && (
                <ConfirmationDialog
                    title="Confirmation de suppression"
                    message={`Êtes-vous sûr de vouloir supprimer ${itemsToDelete.length} canal${itemsToDelete.length > 1 ? 'aux' : ''} ? Cette action est irréversible.`}
                    confirmLabel="Supprimer"
                    cancelLabel="Annuler"
                    onConfirm={handleConfirmDelete}
                    onCancel={handleCancelDelete}
                    danger={true}
                />
            )}
        </div>
    );
};

export default SalesChannelsList;
